import { motion } from 'framer-motion';
import { useState } from 'react';

interface SubscriptionProps {
  onStartSubscription?: (plan: 'monthly' | 'annual') => void;
}

export default function Subscription({ onStartSubscription }: SubscriptionProps) {
  const [selectedPlan, setSelectedPlan] = useState<'monthly' | 'annual'>('monthly');

  const scrollToContact = () => {
    if (onStartSubscription) {
      onStartSubscription(selectedPlan);
    }
  };

  return (
    <section id="subscription" className="py-16 sm:py-24 bg-gradient-to-br from-yellow-100 via-green-100 to-blue-100">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 sm:mb-16"
        >
          <h2 className="text-5xl sm:text-6xl md:text-7xl font-black text-green-500 mb-4 sm:mb-6" style={{ fontFamily: 'Fredoka, system-ui, sans-serif' }}>
            Why Subscribe?
          </h2>
          <p className="text-lg sm:text-xl md:text-2xl text-gray-700 max-w-3xl mx-auto px-4">
            Save money and keep your cans clean all year long!
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
          {/* Left side - Info */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="md:col-span-3 bg-gradient-to-br from-blue-500 to-blue-600 p-16 text-white"
          >
            <h2 className="text-5xl font-black mb-8" style={{ fontFamily: 'Fredoka, system-ui, sans-serif' }}>
              Flexible Cleaning Subscriptions
            </h2>

            <div className="space-y-6 text-xl leading-relaxed">
              <p>
                <span className="bg-yellow-400 text-blue-900 px-3 py-1 rounded-lg font-bold inline-block mb-2">
                  Save Money!
                </span>
                <br />
                Our subscription plans offer amazing discounts compared to one-time cleaning. The more you subscribe, the more you save!
              </p>

              <p>
                <span className="bg-yellow-400 text-blue-900 px-3 py-1 rounded-lg font-bold inline-block mb-2">
                  Stay Clean!
                </span>
                <br />
                Regular cleaning keeps your trash cans fresh and hygienic all year round. No more worrying about bad smells or germs!
              </p>

              <p>
                <span className="bg-yellow-400 text-blue-900 px-3 py-1 rounded-lg font-bold inline-block mb-2">
                  Easy & Flexible!
                </span>
                <br />
                We handle the scheduling automatically. Cancel or change your plan anytime with no hassle. It's that simple!
              </p>

              <p>
                <span className="bg-yellow-400 text-blue-900 px-3 py-1 rounded-lg font-bold inline-block mb-2">
                  Up to 2 Cans Included!
                </span>
                <br />
                All plans include cleaning for up to 2 trash cans. Need more? Just $10 per additional can!
              </p>
            </div>

            <div className="flex items-center gap-6 mt-12">
              <div className="flex items-center gap-2">
                <i className="ri-shield-check-fill text-4xl text-yellow-400"></i>
                <span className="font-semibold">Satisfaction Guaranteed</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-refresh-fill text-4xl text-yellow-400"></i>
                <span className="font-semibold">Cancel Anytime</span>
              </div>
            </div>
          </motion.div>

          {/* Right side - Plan selector */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="md:col-span-2 bg-red-500 p-12 flex flex-col items-center justify-center"
          >
            <div className="bg-white rounded-[40px] p-10 w-full shadow-xl">
              <h3 className="text-3xl font-bold text-blue-600 text-center mb-8" style={{ fontFamily: 'Fredoka, system-ui, sans-serif' }}>
                Subscribe & Save
              </h3>

              {/* Toggle switch */}
              <div className="bg-gray-100 p-2 rounded-full flex items-center mb-8">
                <button
                  onClick={() => setSelectedPlan('monthly')}
                  className={`flex-1 py-4 rounded-full font-bold text-lg transition-all whitespace-nowrap cursor-pointer ${
                    selectedPlan === 'monthly'
                      ? 'bg-yellow-400 text-blue-900 shadow-lg'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Monthly
                </button>
                <button
                  onClick={() => setSelectedPlan('quarterly')}
                  className={`flex-1 py-4 rounded-full font-bold text-lg transition-all whitespace-nowrap cursor-pointer ${
                    selectedPlan === 'quarterly'
                      ? 'bg-yellow-400 text-blue-900 shadow-lg'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Quarterly
                </button>
              </div>

              {/* Savings badge */}
              <motion.div
                key={selectedPlan}
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.3 }}
                className="text-center mb-8"
              >
                <div className="inline-flex items-center justify-center w-32 h-32 bg-gradient-to-br from-yellow-300 to-yellow-500 rounded-full border-8 border-yellow-200 shadow-lg">
                  <div className="text-center">
                    <div className="text-3xl font-black text-blue-900" style={{ fontFamily: 'Fredoka, system-ui, sans-serif' }}>
                      {selectedPlan === 'monthly' ? '71%' : '50%'}
                    </div>
                    <div className="text-sm font-bold text-blue-900 -mt-1">OFF</div>
                  </div>
                </div>
                <p className="text-gray-600 mt-4 text-lg font-semibold">
                  {selectedPlan === 'monthly' ? 'Save $50 per clean!' : 'Save $35 per clean!'}
                </p>
              </motion.div>

              <button
                onClick={scrollToContact}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-5 rounded-full text-xl font-bold shadow-lg hover:scale-105 transition-all whitespace-nowrap cursor-pointer"
              >
                Start Subscription
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}